import { getAPI, postAPI } from 'apis/backend/apiNode';
import CreationModal from 'components/common/CreationModal';
import { clientCreationFields, formatClientData } from 'components/common/fieldsConfig';
import SearchableDropdown from 'components/common/SearchableDropdown';
import showNotification from 'components/extras/showNotification';
import AuthContext from 'contexts/authContext';
import CompanieContext from 'contexts/companieContext';
import React, { useCallback, useContext, useState } from 'react';
import { Modal } from 'react-bootstrap';

import Icon from 'components/icon/Icon';
import Spinner from 'components/bootstrap/Spinner';
import Select from 'components/bootstrap/forms/Select';

import { getTaxInfo } from 'common/function/functionsUtils';
import { INTERVENTION_MOTIFS } from 'pages/planning/hooks/planningService';

const NewDevisModal = ({
    show,
    onHide,
    fromIntervention = false,
    interventionData,
    onSuccess,
}) => {
    const { companie } = useContext(CompanieContext);
    const { userData } = useContext(AuthContext);

    const country = (
        fromIntervention
            ? interventionData?.companyId?.address?.country
            : companie?.address?.country || 'france'
    )?.toLowerCase() || 'france';

    const { defaultTax } = getTaxInfo(country);

    const [selectedClient, setSelectedClient] = useState(
        fromIntervention ? interventionData?.client : null
    );

    const [isCreating, setIsCreating] = useState(false);

    const [devisTitle, setDevisTitle] = useState('');

    const [motif, setMotif] = useState(
        fromIntervention ? interventionData?.motif || '' : ''
    );

    const defaultDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];

    // Techniciens extraction
    const technicienIds =
        interventionData?.techniciens?.length > 0
            ? interventionData.techniciens
                  .map((t) => t?._id || t?.id)
                  .filter(Boolean)
            : (interventionData?.cars || [])
                  .flatMap((car) => car?.conducteurs || [])
                  .map((c) => {
                      const tech = c?.technicien;
                      return typeof tech === 'object'
                          ? tech?._id || tech?.id
                          : tech;
                  })
                  .filter(Boolean);

    const handleConfirmCreation = async () => {
        try {
            setIsCreating(true);

            const payload = {
                name: devisTitle,

                companyId: fromIntervention
                    ? interventionData?.companyId?._id ||
                      interventionData?.companyId
                    : companie?._id,

                clientId: fromIntervention
                    ? interventionData?.client?._id ||
                      interventionData?.client
                    : selectedClient?._id,

                dateValidite: defaultDate,

                createdBy: userData?.id,

                technicienIds: fromIntervention ? technicienIds : [],

                motif: motif || 'autre',

                origin: fromIntervention ? 'technicien' : 'apl',

                interventionId: fromIntervention
                    ? interventionData?._id || interventionData?.id
                    : null,

                tva: defaultTax,
            };

            const result = await postAPI('devis', payload);

            if (result?.success) {
                const createdDevis = result?.data || result?.data?.devis;

                showNotification(
                    'Succès',
                    'Devis créé avec succès',
                    'success'
                );

                if (onSuccess) {
                    onSuccess(createdDevis);
                }

                onHide();
            } else {
                showNotification(
                    'Erreur',
                    result?.message || 'Échec de la création',
                    'danger'
                );
            }
        } catch (error) {
            showNotification(
                'Erreur',
                'Erreur lors de la création',
                'danger'
            );
        } finally {
            setIsCreating(false);
        }
    };

    const fetchClients = useCallback(async (searchTerm = '', signal = null) => {
        try {
            const queryParams = new URLSearchParams({
                limit: '20',
                page: '1',
            });

            if (searchTerm) {
                queryParams.append('search', searchTerm);
            }

            const result = await getAPI(
                'store2000_client',
                `?${queryParams.toString()}`,
                signal
            );

            if (result?.success) {
                return result.clients || [];
            }

            return [];
        } catch (error) {
            return [];
        }
    }, []);

    const renderClientOption = useCallback((client) => {
        const mainEmail = client?.emails?.[0] || '';
        const mainPhone = client?.telephones?.[0] || '';

        return (
            <div className='dropdown-option'>
                <div className='d-flex align-items-center justify-content-between'>
                    <div className='flex-grow-1'>
                        <div className='fw-medium text-dark'>
                            {client?.nom}
                        </div>

                        {mainEmail && (
                            <small className='text-muted d-block mt-1'>
                                <i className='fas fa-envelope me-1'></i>
                                {mainEmail}
                            </small>
                        )}

                        {mainPhone && (
                            <small className='text-muted d-block mt-1'>
                                <i className='fas fa-phone me-1'></i>
                                {mainPhone}
                            </small>
                        )}
                    </div>
                </div>
            </div>
        );
    }, []);

    if (!show) return null;

    const isBtnDisabled =
        isCreating ||
        !devisTitle ||
        !motif ||
        (!fromIntervention && !selectedClient);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size='lg'
            backdrop='static'
            keyboard={false}
            centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Créer un nouveau devis
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Titre */}
                <div className='form-group mb-3'>
                    <label className='mb-2 fw-semibold'>
                        Titre du devis *
                    </label>

                    <input
                        type='text'
                        className='form-control'
                        placeholder='Ex: Réparation volet roulant'
                        value={devisTitle}
                        onChange={(e) => setDevisTitle(e.target.value)}
                        disabled={isCreating}
                        autoFocus
                    />
                </div>

                {/* Motif */}
                <div className='form-group mb-3'>
                    <label className='mb-2 fw-semibold'>
                        Motif *
                    </label>

                    <Select
                        value={motif}
                        onChange={(e) => setMotif(e.target.value)}
                        disabled={isCreating}>
                        <option value=''>
                            Sélectionnez un motif
                        </option>

                        {[
	{ value: 'rideaux', label: 'Rideaux' },
	{ value: 'volet', label: 'Volet roulant' },
	{ value: 'store', label: 'Store' },
	{ value: 'porte', label: 'Porte' },
	{ value: 'portail', label: 'Portail' },
].map((m) => (
                            <option
                                key={m.value}
                                value={m.value}>
                                {m.label}
                            </option>
                        ))}
                    </Select>
                </div>

                {/* Client */}
                {!fromIntervention && (
                    <div className='form-group mb-3'>
                        <label className='mb-2 fw-semibold'>
                            Client *
                        </label>

                        <SearchableDropdown
                            value={selectedClient}
                            onChange={(c) => setSelectedClient(c)}
                            fetchOptions={fetchClients}
                            placeholder='Rechercher un client...'
                            required={true}
                            displayField='nom'
                            subField='emails.0'
                            renderOption={renderClientOption}
                            showAddButton={true}
                            disabled={isCreating}
                            modalComponent={CreationModal}
                            modalProps={{
                                title: 'Créer un nouveau client',

                                fields: clientCreationFields,

                                submit: async (clientData) => {
                                    const response = await postAPI(
                                        'store2000_client',
                                        formatClientData(clientData)
                                    );

                                    if (response.success) {
                                        return (
                                            response.client ||
                                            response.data
                                        );
                                    }

                                    throw new Error(
                                        response.error || 'Erreur'
                                    );
                                },

                                submitLabel: 'Créer le client',

                                onSuccess: (newClient) => {
                                    if (newClient) {
                                        setSelectedClient(newClient);
                                    }
                                },
                            }}
                        />
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button
                    type='button'
                    className='btn btn-secondary px-4'
                    onClick={onHide}
                    disabled={isCreating}>
                    Annuler
                </button>

                <button
                    type='button'
                    className={`btn d-flex align-items-center px-4 ${
                        isBtnDisabled
                            ? 'btn-light text-muted'
                            : 'btn-primary shadow-sm'
                    }`}
                    onClick={handleConfirmCreation}
                    disabled={isBtnDisabled}>
                    {isCreating ? (
                        <>
                            <span
                                className='spinner-border spinner-border-sm me-2'
                                role='status'
                                aria-hidden='true'></span>

                            Création...
                        </>
                    ) : (
                        <>
                            <Icon
                                icon='Add'
                                className='me-2'
                            />
                            Créer le devis
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default NewDevisModal;